import React, { useState, useRef, useCallback, useMemo } from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
} from "@material-ui/core";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/system";
import { tooltipClasses } from "@mui/material/Tooltip";
import { useColumnWidths } from "./hooks/useColumnWidths";
import { TableHeader } from "./components/TableHeader";
import { TableCell } from "./components/TableCell";
import { FilterPopover } from "./components/FilterPopover";
import { applyFilter } from "./utils/filterHelpers";
import { copyTableToClipboard } from "./utils/copyToClipboard";

// Custom tooltip styled to match CopyObjectIcon
const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "6.9rem",
    fontSize: "0.9rem",
    color: "black",
    textAlign: "center",
    background: "white",
    border: "0.5px solid lightgrey",
  },
});

// Main Resizable Table Component
export const ResizableTable = ({
  columns,
  rows,
  currencySymbol,
  onCellChange,
  getCellValue,
  getCellStyle,
  rowKey = "label",
  stickyFirstColumn = false,
  hideSubHeaderRow = false,
  onRowClick,
  heading,
  maxWidth = "100vw",
  getRowTooltip, // Optional function that returns tooltip props for a row: (row) => ({ title, placement, followCursor, TooltipComponent }) or null
}) => {
  const [columnWidths, updateColumnWidth] = useColumnWidths(columns);
  const [resizing, setResizing] = useState(null);
  const [sortConfig, setSortConfig] = useState(null);
  const [filters, setFilters] = useState({});
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [copyTooltipText, setCopyTooltipText] = useState("Copy table to clipboard");
  const tableRef = useRef(null);
  const firstHeaderRowRef = useRef(null);
  const [firstRowHeight, setFirstRowHeight] = useState(36); // Default fallback

  // Measure first header row height for accurate sub-header positioning
  React.useEffect(() => {
    let resizeObserver = null;
    let rafId1 = null;
    let rafId2 = null;
    
    const measureHeight = () => {
      if (firstHeaderRowRef.current) {
        const height = firstHeaderRowRef.current.offsetHeight;
        if (height > 0) {
          setFirstRowHeight(height);
        }
      }
    };
    
    // Measure after DOM is fully rendered using requestAnimationFrame
    rafId1 = requestAnimationFrame(() => {
      // Double RAF to ensure layout is complete
      rafId2 = requestAnimationFrame(() => {
        measureHeight();
        
        // Set up ResizeObserver after initial measurement
        if (firstHeaderRowRef.current) {
          resizeObserver = new ResizeObserver(() => {
            measureHeight();
          });
          resizeObserver.observe(firstHeaderRowRef.current);
        }
      });
    });
    
    return () => {
      if (rafId1 !== null) {
        cancelAnimationFrame(rafId1);
      }
      if (rafId2 !== null) {
        cancelAnimationFrame(rafId2);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [columns, columnWidths]);

  // Sorting function
  const sortRows = useCallback((rowsToSort, field, direction, cellType) => {
    const sorted = [...rowsToSort].sort((a, b) => {
      // For numeric/currency/percentage/date types, use raw values from row directly
      // to avoid parsing formatted strings
      let aValue, bValue;
      if (cellType === "number" || cellType === "currency" || cellType === "percentage" || cellType === "date") {
        // Use raw field value directly for proper sorting
        aValue = a[field];
        bValue = b[field];
      } else {
        // For text types, use getCellValue if available (may format the value)
        aValue = getCellValue ? getCellValue(a, field) : a[field];
        bValue = getCellValue ? getCellValue(b, field) : b[field];
      }

      // Handle null/undefined values for numeric types - place them at the end
      const aIsNull = (aValue == null || aValue === "");
      const bIsNull = (bValue == null || bValue === "");

      // Convert to numbers for numeric types
      if (cellType === "number" || cellType === "currency" || cellType === "percentage") {
        // Handle null values: place them at the end regardless of sort direction
        if (aIsNull && bIsNull) return 0;
        if (aIsNull) return 1; // null values go to end
        if (bIsNull) return -1; // null values go to end
        
        const aNum = typeof aValue === "number" ? aValue : parseFloat(String(aValue));
        const bNum = typeof bValue === "number" ? bValue : parseFloat(String(bValue));
        
        // Handle NaN cases
        if (isNaN(aNum) && isNaN(bNum)) return 0;
        if (isNaN(aNum)) return 1;
        if (isNaN(bNum)) return -1;
        
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      // Handle null/undefined values for text types
      if (aValue == null || aValue === "-") aValue = "";
      if (bValue == null || bValue === "-") bValue = "";

      // Handle date sorting
      if (cellType === "date") {
        const aDate = aValue ? new Date(aValue).getTime() : 0;
        const bDate = bValue ? new Date(bValue).getTime() : 0;
        if (isNaN(aDate)) return direction === "asc" ? -1 : 1;
        if (isNaN(bDate)) return direction === "asc" ? 1 : -1;
        return direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      // String comparison for text
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (direction === "asc") {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });

    return sorted;
  }, [getCellValue]);

  // Apply filters to rows
  const filteredRows = useMemo(() => {
    if (Object.keys(filters).length === 0) return rows;

    // When filters are active, exclude Total, Quota, and Other Opps rows and apply filters to regular rows only
    return rows.filter((row) => {
      // Exclude Total, Quota, and Other Opps rows when filters are active
      const label = row.label || "";
      if (label === "Total" || label === "total" || label === "Quota" || label === "quota" || label === "Other Opps" || label === "other opps") {
        return false;
      }

      return Object.entries(filters).every(([field, filter]) => {
        // Find the column config for the field type
        let fieldType = undefined;
        for (const col of columns) {
          for (const subCol of col.subColumns) {
            if (subCol.field === field) {
              // Use dataType if available (for more accurate filtering), otherwise use type
              fieldType = subCol.dataType || subCol.type;
              break;
            }
          }
          if (fieldType) break;
        }

        return applyFilter(row, field, filter.operator, filter.value, fieldType, getCellValue);
      });
    });
  }, [rows, filters, columns, getCellValue]);

  // Get sorted and filtered rows
  const sortedRows = useMemo(() => {
    const rowsToSort = filteredRows;
    if (!sortConfig) return rowsToSort;
    
    // Separate special rows (Total, Quota, Other Opps) from regular rows
    const specialRows = rowsToSort.filter((row) => {
      const label = row.label || "";
      return label === "Total" || label === "total" || 
             label === "Quota" || label === "quota" || 
             label === "Other Opps" || label === "other opps";
    });
    const regularRows = rowsToSort.filter((row) => {
      const label = row.label || "";
      return label !== "Total" && label !== "total" && 
             label !== "Quota" && label !== "quota" && 
             label !== "Other Opps" && label !== "other opps";
    });
    
    // Find the column config for the sort field
    let cellType = undefined;
    for (const col of columns) {
      for (const subCol of col.subColumns) {
        if (subCol.field === sortConfig.field) {
          cellType = subCol.type;
          break;
        }
      }
      if (cellType) break;
    }

    // Sort only regular rows
    const sortedRegularRows = sortRows(regularRows, sortConfig.field, sortConfig.direction, cellType);
    
    // Special rows are already in their original order from filteredRows, so just append them
    // Always append special rows at the bottom in their original order
    return [...sortedRegularRows, ...specialRows];
  }, [filteredRows, sortConfig, sortRows, columns]);

  // Handle sort click
  const handleSort = useCallback((field) => {
    setSortConfig((current) => {
      if (current?.field === field) {
        // Toggle direction if same field
        return {
          field,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      } else {
        // New field, default to ascending
        return { field, direction: "asc" };
      }
    });
  }, []);

  // Handle filter apply
  const handleFilterApply = useCallback((field, operator, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: { operator, value },
    }));
  }, []);

  // Handle filter clear
  const handleFilterClear = useCallback((field) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  }, []);

  // Handle filter popover open
  const handleFilterClick = useCallback((event, field, fieldType) => {
    setFilterAnchorEl({ element: event.currentTarget, field, fieldType });
  }, []);

  // Handle filter popover close
  const handleFilterPopoverClose = useCallback(() => {
    setFilterAnchorEl(null);
  }, []);

  // Flatten all columns for header calculation
  const allColumns = useMemo(() => {
    const flat = [];
    columns.forEach((mainCol, mainIdx) => {
      mainCol.subColumns.forEach((subCol, subIdx) => {
        flat.push({ main: mainCol, sub: subCol, mainIdx, subIdx });
      });
    });
    return flat;
  }, [columns]);

  const handleMouseDown = useCallback(
    (e, key, isMainColumn = false) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      
      let startWidth;
      
      if (isMainColumn) {
        // Extract column index from key (key format is "main-0", "main-1", etc.)
        const colIdx = parseInt(key.replace("main-", ""), 10);
        const mainCol = columns[colIdx];
        startWidth = mainCol?.subColumns.reduce((sum, subCol, subIdx) => {
          const subKey = `${colIdx}-${subIdx}`;
          return sum + (columnWidths[subKey] || subCol.width || subCol.minWidth || 120);
        }, 0) || 300;
      } else {
        // Extract indices from key (key format is "mainIdx-subIdx")
        const [mainIdx, subIdx] = key.split("-").map(Number);
        startWidth = columnWidths[key] || 120;
      }
      
      setResizing({ key, startX, startWidth });

      const handleMouseMove = (moveEvent) => {
        const diff = moveEvent.clientX - startX;
        const newWidth = startWidth + diff;
        
        updateColumnWidth(key, newWidth, isMainColumn);
      };

      const handleMouseUp = () => {
        setResizing(null);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [columnWidths, updateColumnWidth, columns, allColumns]
  );

  // Calculate minimum width based on sum of all column widths
  const minTableWidth = useMemo(() => {
    let totalWidth = 0;
    columns.forEach((mainCol, mainIdx) => {
      mainCol.subColumns.forEach((subCol, subIdx) => {
        const key = `${mainIdx}-${subIdx}`;
        const width = columnWidths[key] || subCol.width || subCol.minWidth || 120;
        totalWidth += width;
      });
    });
    return totalWidth;
  }, [columns, columnWidths]);

  // Function to copy table data to clipboard
  const handleCopyToClipboard = useCallback(async () => {
    await copyTableToClipboard({
      columns,
      rows: sortedRows,
      currencySymbol,
      getCellValue,
      getCellStyle,
      hideSubHeaderRow
    });
    // Update tooltip text to show "Table copied"
    setCopyTooltipText("Table copied");
    // Reset the message after 3 seconds
    setTimeout(() => {
      setCopyTooltipText("Copy table to clipboard");
    }, 3000);
  }, [columns, sortedRows, currencySymbol, getCellValue, getCellStyle, hideSubHeaderRow]);

  return (
    <Box style={{ width: maxWidth, maxWidth: maxWidth, overflow: "hidden", boxSizing: "border-box" }}>
      <Box style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 16 }}>
        {heading && (
          <Typography
            variant="h6"
            style={{
              fontSize: "1rem",
              fontWeight: 700,
              marginRight: "auto",
            }}
          >
            {heading}
          </Typography>
        )}
        <CustomWidthTooltip title={copyTooltipText} placement="left">
          <IconButton
            size="small"
            onClick={handleCopyToClipboard}
            style={{
              color: "#A9A9A9",
              backgroundColor: "#ffffff",
              transition: "background-color 0.3s",
              width: "32px",
              height: "30px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e6e3e3";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#ffffff";
            }}
          >
            <img 
              src="/Copy_To_Clipboard_Icon.svg" 
              alt="Copy to clipboard" 
              style={{
                width: "20px",
                height: "20px",
                color: "#A9A9A9",
                fill: "#A9A9A9",
              }} 
            />
          </IconButton>
        </CustomWidthTooltip>
      </Box>
      <TableContainer 
        component={Paper} 
        style={{ 
          maxHeight: "80vh", 
          overflowX: "auto",
          overflowY: "auto",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <Table 
          ref={tableRef} 
          stickyHeader
          style={{
            minWidth: `${minTableWidth}px`,
            width: "100%",
            // width: `${minTableWidth}px`,
            tableLayout: "fixed",
            border: "1px solid rgba(224, 224, 224, 1)",
            borderCollapse: "separate",
          }}
        >
          {/* Two-level header structure */}
          <TableHead>
            <TableHeader
              columns={columns}
              allColumns={allColumns}
              columnWidths={columnWidths}
              firstRowHeight={firstRowHeight}
              resizing={resizing}
              sortConfig={sortConfig}
              filters={filters}
              onSort={handleSort}
              onFilterClick={handleFilterClick}
              onMouseDown={handleMouseDown}
              firstHeaderRowRef={firstHeaderRowRef}
              stickyFirstColumn={stickyFirstColumn}
              hideSubHeaderRow={hideSubHeaderRow}
            />
          </TableHead>
          <TableBody>
            {sortedRows.map((row, rowIndex) => {
              // Find the original row index in the unsorted rows
              const originalRowIndex = rows.findIndex(r => (r[rowKey] || r.id || r.label) === (row[rowKey] || row.id || row.label));
              const actualRowIndex = originalRowIndex >= 0 ? originalRowIndex : rowIndex;
              
              // Get tooltip configuration for this row if getRowTooltip is provided
              const tooltipConfig = getRowTooltip ? getRowTooltip(row) : null;
              const TooltipComponent = tooltipConfig?.TooltipComponent || null;
              const rowKeyValue = row[rowKey] || rowIndex;
              
              const rowContent = (
                <TableRow 
                  key={tooltipConfig ? undefined : rowKeyValue}
                  onClick={onRowClick ? () => onRowClick(row, actualRowIndex) : undefined}
                  style={{
                    backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f5f5f5", // Alternating rows: white and light grey
                    cursor: onRowClick ? "pointer" : "default",
                  }}
                  onMouseEnter={(e) => {
                    if (onRowClick) {
                      e.currentTarget.style.backgroundColor = rowIndex % 2 === 0 ? "#f0f0f0" : "#e8e8e8";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = rowIndex % 2 === 0 ? "#ffffff" : "#f5f5f5";
                  }}
                >
                {allColumns.map(({ sub, main, mainIdx, subIdx }) => {
                  const key = `${mainIdx}-${subIdx}`;
                  const isFirstColumn = mainIdx === 0 && subIdx === 0 && stickyFirstColumn;
                  return (
                    <TableCell
                      key={key}
                      row={row}
                      rowIndex={rowIndex}
                      config={sub}
                      colKey={key}
                      columnWidths={columnWidths}
                      currencySymbol={currencySymbol}
                      getCellValue={getCellValue}
                      getCellStyle={getCellStyle}
                      onCellChange={onCellChange}
                      isFirstColumn={isFirstColumn}
                      mainHeader={main.label}
                      onCellClick={onRowClick ? (columnInfo) => {
                        onRowClick(row, actualRowIndex, columnInfo);
                      } : undefined}
                    />
                  );
                })}
                </TableRow>
              );

              // Wrap row with tooltip if tooltip config is provided
              if (tooltipConfig && TooltipComponent) {
                return (
                  <TooltipComponent
                    key={rowKeyValue}
                    title={tooltipConfig.title || ""}
                    placement={tooltipConfig.placement || "top"}
                    followCursor={tooltipConfig.followCursor || false}
                  >
                    {rowContent}
                  </TooltipComponent>
                );
              }

              return rowContent;
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {filterAnchorEl && (
        <FilterPopover
          open={!!filterAnchorEl}
          anchorEl={filterAnchorEl.element}
          onClose={handleFilterPopoverClose}
          field={filterAnchorEl.field}
          fieldType={filterAnchorEl.fieldType}
          onApply={(operator, value) => {
            handleFilterApply(filterAnchorEl.field, operator, value);
            handleFilterPopoverClose();
          }}
          onClear={() => {
            handleFilterClear(filterAnchorEl.field);
            handleFilterPopoverClose();
          }}
          currentFilter={filters[filterAnchorEl.field] || null}
        />
      )}
    </Box>
  );
};

